import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  AlertTriangle, 
  Package,
  TrendingUp,
  Search,
  X,
  Edit,
  Save
} from 'lucide-react';
import { fetchChemicals, updateChemicalStock, updateChemicalPurchasePrice } from '../../api/chemicals';
import styles from './ChemicalManagement.module.scss';

const ChemicalManagement = () => {
  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [stockUpdate, setStockUpdate] = useState({ quantity: '', unit: 'g', action: 'increase' });
  const [priceUpdate, setPriceUpdate] = useState({ price: '' });
  const [search, setSearch] = useState('');
  const [thresholdFilter, setThresholdFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalChemicals, setTotalChemicals] = useState(0);
  const [totalLowStock, setTotalLowStock] = useState(0);
  const [totalNormalStock, setTotalNormalStock] = useState(0);

  const getServerFilterType = useCallback(() => {
    if (thresholdFilter === 'low') return 'below_threshold';
    if (thresholdFilter === 'normal') return 'normal_stock';
    return 'all';
  }, [thresholdFilter]);

  const loadChemicals = useCallback(async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await fetchChemicals({
        search: search ? search.trim() : null,
        skip,
        limit: itemsPerPage,
        filterType: getServerFilterType(),
      });
      const list = response?.chemicals || response || [];
      setChemicals(list);
      setTotalChemicals(response?.total ?? list.length);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, itemsPerPage, getServerFilterType]);

  const loadSummaryCounts = useCallback(async () => {
    try {
      const searchTerm = search ? search.trim() : null;
      const [allRes, lowRes, normalRes] = await Promise.all([
        fetchChemicals({ search: searchTerm, skip: 0, limit: 1, filterType: 'all' }),
        fetchChemicals({ search: searchTerm, skip: 0, limit: 1, filterType: 'below_threshold' }),
        fetchChemicals({ search: searchTerm, skip: 0, limit: 1, filterType: 'normal_stock' }),
      ]);

      setTotalChemicals(allRes?.total ?? 0);
      setTotalLowStock(lowRes?.total ?? 0);
      setTotalNormalStock(normalRes?.total ?? 0);
    } catch (err) {
      setError(err.message);
    }
  }, [search]);

  useEffect(() => {
    // Debounce typing so we don't spam requests.
    // Important: we fetch list + totals from the same timer to avoid "double reloads".
    const t = setTimeout(() => {
      loadChemicals();
      loadSummaryCounts();
    }, 400);
    return () => clearTimeout(t);
  }, [loadChemicals, loadSummaryCounts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, thresholdFilter]);

  const handleStockUpdate = async (chemicalId) => {
    try {
      const quantity = parseFloat(stockUpdate.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        setError('Please enter a valid quantity');
        return;
      }

      // Convert to the chemical's base unit (assuming all chemicals use 'g' as base unit)
      let changeAmount = quantity;
      if (stockUpdate.unit === 'kg') {
        changeAmount = quantity * 1000; // Convert kg to g
      }

      changeAmount = stockUpdate.action === 'increase' ? changeAmount : -changeAmount;
      
      await updateChemicalStock(chemicalId, changeAmount);
      
      // Update only the specific chemical instead of reloading all
      setChemicals(prevChemicals => 
        prevChemicals.map(chemical => {
          if (chemical.id === chemicalId) {
            return {
              ...chemical,
              available_qty: chemical.available_qty + changeAmount
            };
          }
          return chemical;
        })
      );
      
      setEditingStock(null);
      setStockUpdate({ quantity: '', unit: 'g', action: 'increase' });
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePriceUpdate = async (chemicalId) => {
    try {
      const price = parseFloat(priceUpdate.price);
      if (isNaN(price) || price < 0) {
        setError('Please enter a valid price (must be 0 or greater)');
        return;
      }

      await updateChemicalPurchasePrice(chemicalId, price);
      
      // Update only the specific chemical instead of reloading all
      setChemicals(prevChemicals => 
        prevChemicals.map(chemical => {
          if (chemical.id === chemicalId) {
            return {
              ...chemical,
              purchase_price: price
            };
          }
          return chemical;
        })
      );

      setEditingPrice(null);
      setPriceUpdate({ price: '' });
      setError(null);
    } catch (err) {
        setError(err.message);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setThresholdFilter('all');
    setCurrentPage(1);
  };

  const filteredChemicals = useMemo(() => chemicals, [chemicals]);

  const paginatedChemicals = useMemo(() => {
    return filteredChemicals;
  }, [filteredChemicals]);

  const totalPages = Math.ceil(totalChemicals / itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return `₹ ${price.toFixed(2)}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Stock Management</h2>
        <p>Manage chemical inventory and stock levels</p>
      </div>

      {error && (
        <div className={styles.error}>
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={20} />
            <input
              type="text"
              placeholder="Search chemicals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
        </div>

        <div className={styles.filterGroup}>
          <select
            value={thresholdFilter}
            onChange={(e) => setThresholdFilter(e.target.value)}
          >
            <option value="all">All Stock Levels</option>
            <option value="low">Low Stock</option>
            <option value="normal">Normal Stock</option>
          </select>
        </div>

        <button onClick={clearFilters} className={styles.clearButton}>
          Clear Filters
        </button>
      </div>

      {/* Summary Stats */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <Package size={24} />
          <div>
            <h3>{totalChemicals}</h3>
            <p>Total Chemicals</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <AlertTriangle size={24} />
          <div>
            <h3>{totalLowStock}</h3>
            <p>Low Stock</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <TrendingUp size={24} />
          <div>
            <h3>{totalNormalStock}</h3>
            <p>Normal Stock</p>
          </div>
        </div>
      </div>

      {/* Chemicals Table */}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Chemical Name</th>
                  <th>Available Quantity</th>
                  <th>Threshold</th>
                  <th>Unit</th>
                  <th>Status</th>
              <th>Purchase Price</th>
              <th>Last Purchase</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && paginatedChemicals.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={styles.loadingRow}>
                      Loading chemicals...
                    </td>
                  </tr>
                ) : (
                  paginatedChemicals.map((chemical) => (
              <tr key={chemical.id} className={chemical.available_qty <= chemical.threshold_qty ? styles.lowStock : ''}>
                <td>
                  <div className={styles.chemicalName}>
                          <Package size={16} />
                          <span>{chemical.name}</span>
                        </div>
                      </td>
                <td>
                  <span className={styles.quantity}>
                    {chemical.available_qty.toFixed(3)}
                        </span>
                      </td>
                <td>
                  <span className={styles.threshold}>
                    {chemical.threshold_qty.toFixed(3)}
                        </span>
                      </td>
                <td>
                  <span className={styles.unit}>{chemical.unit}</span>
                      </td>
                <td>
                  <span className={`${styles.status} ${chemical.available_qty <= chemical.threshold_qty ? styles.low : styles.normal}`}>
                    {chemical.available_qty <= chemical.threshold_qty ? 'Low Stock' : 'Normal'}
                          </span>
                      </td>
                <td>
                  {editingPrice === chemical.id ? (
                    <div className={styles.editForm}>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Price per unit"
                        value={priceUpdate.price}
                        onChange={(e) => setPriceUpdate(prev => ({ ...prev, price: e.target.value }))}
                      />
                      <button
                        onClick={() => handlePriceUpdate(chemical.id)}
                        className={styles.saveButton}
                      >
                        <Save size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingPrice(null);
                          setPriceUpdate({ price: '' });
                        }}
                        className={styles.cancelButton}
                      >
                        <X size={16} />
                      </button>
                            </div>
                          ) : (
                    <div className={styles.priceContainer}>
                      <span className={styles.price}>
                        {formatPrice(chemical.purchase_price)}
                      </span>
                      <button
                        onClick={() => {
                          setEditingPrice(chemical.id);
                          setPriceUpdate({ price: chemical.purchase_price?.toString() || '' });
                        }}
                        className={styles.editPriceButton}
                        title="Edit purchase price"
                      >
                        <Edit size={14} />
                      </button>
                            </div>
                          )}
                </td>
                <td>
                  <span className={styles.date}>
                    {formatDate(chemical.last_purchase)}
                                </span>
                      </td>
                <td>
                  <div className={styles.actions}>
                    {editingStock === chemical.id ? (
                      <div className={styles.editForm}>
                        <input
                          type="number"
                          step="0.001"
                          placeholder="Quantity"
                          value={stockUpdate.quantity}
                          onChange={(e) => setStockUpdate(prev => ({ ...prev, quantity: e.target.value }))}
                        />
                        <select
                          value={stockUpdate.unit}
                          onChange={(e) => setStockUpdate(prev => ({ ...prev, unit: e.target.value }))}
                        >
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                        </select>
                        <select
                          value={stockUpdate.action}
                          onChange={(e) => setStockUpdate(prev => ({ ...prev, action: e.target.value }))}
                        >
                          <option value="increase">Increase</option>
                          <option value="decrease">Decrease</option>
                        </select>
                        <button
                          onClick={() => handleStockUpdate(chemical.id)}
                          className={styles.saveButton}
                        >
                          <Save size={16} />
                        </button>
                          <button
                          onClick={() => {
                            setEditingStock(null);
                            setStockUpdate({ quantity: '', unit: 'g', action: 'increase' });
                          }}
                          className={styles.cancelButton}
                        >
                          <X size={16} />
                          </button>
                  </div>
                ) : (
                  <button
                        onClick={() => setEditingStock(chemical.id)}
                        className={styles.editButton}
                      >
                        <Edit size={16} />
                        Update Stock
                  </button>
                )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {!loading && paginatedChemicals.length === 0 && (
        <div className={styles.noData}>
          <Package size={48} />
          <h3>No chemicals found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default ChemicalManagement;